import { Link } from "react-router-dom";
import { CalendarDays, Clock, Tag, XCircle, AlertTriangle, ArrowRight } from "lucide-react";
import { ReEnrolButton } from "@/components/ReEnrolButton";

const fmtDate = (iso, opts) => (iso ? new Date(iso).toLocaleString("en-GB", opts) : "—");
const typeLabel = (t) => (t ? String(t).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Session");

function Item({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-start gap-3" data-testid={testid}>
      <Icon className="w-4 h-4 text-[#A3A3A3] mt-0.5 shrink-0" />
      <div>
        <div className="label-mono text-[#737373]">{label}</div>
        <div className="text-sm text-[#E5E5E5]">{value}</div>
      </div>
    </div>
  );
}

export function CancelledSessionSummary({ s, onReEnrolled }) {
  const end = s.start ? new Date(new Date(s.start).getTime() + (s.duration_min || 60) * 60000) : null;
  const time = s.start ? `${fmtDate(s.start, { hour: "2-digit", minute: "2-digit" })} – ${end.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}` : "—";
  return (
    <div className="max-w-2xl space-y-6" data-testid="cancelled-session-page">
      <div>
        <div className="label-mono mb-2 inline-flex items-center gap-2 text-[#FF4500]" data-testid="cancelled-session-status">
          <XCircle className="w-3.5 h-3.5" /> {s.rescheduled_to ? "RESCHEDULED" : s.removed_by_coach ? `REMOVED BY ${(s.cancelled_by_name ? `COACH ${s.cancelled_by_name}` : "COACH").toUpperCase()}` : "CANCELLED"}
        </div>
        <h1 className="h-display text-4xl sm:text-5xl line-through decoration-[#FF4500]/60" data-testid="cancelled-session-title">{s.title}</h1>
      </div>
      <div className="card-surface p-5 grid sm:grid-cols-2 gap-5">
        <Item icon={CalendarDays} label="Date" testid="cancelled-session-date"
          value={fmtDate(s.start, { weekday: "long", day: "numeric", month: "long", year: "numeric" })} />
        <Item icon={Clock} label="Time" testid="cancelled-session-time" value={time} />
        <Item icon={Tag} label="Session type" testid="cancelled-session-type" value={typeLabel(s.session_type)} />
        <Item icon={XCircle} label={s.rescheduled_to ? "Rescheduled on" : "Cancelled on"} testid="cancelled-session-cancelled-at"
          value={fmtDate(s.cancelled_at, { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })} />
      </div>
      {s.late_cancellation && !s.removed_by_coach && (
        <div className="card-surface p-4 border-l-4 border-[#FF9F1C] flex items-start gap-3 text-sm" data-testid="cancelled-session-late-note">
          <AlertTriangle className="w-4 h-4 text-[#FF9F1C] mt-0.5 shrink-0" />
          <span>
            This was cancelled inside the 48-hour cut-off, so the {s.payment_status === "credit" ? "credit was" : "payment was"} not
            automatically returned. Your coach can re-credit you if there was an agreed reason.
          </span>
        </div>
      )}
      {s.cancel_reason && !s.removed_by_coach && (
        <div className="text-sm text-[#A3A3A3]" data-testid="cancelled-session-reason">Your reason: “{s.cancel_reason}”</div>
      )}
      {s.rescheduled_to && (
        <div>
          <Link to={`/session/${s.rescheduled_to}`} data-testid="cancelled-session-new-link"
            className="min-h-[44px] px-4 py-2 text-xs uppercase tracking-wider font-bold bg-[#CCFF00] text-black inline-flex items-center gap-2">
            View the new session <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}
      <ReEnrolButton rebook={s.rebook} onDone={onReEnrolled} />
      <Link to="/calendar" className="text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-white inline-flex items-center gap-1" data-testid="cancelled-session-calendar-link">
        Back to Calendar <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  );
}
